import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    Plus,
    FileText,
    Download,
    Trash2,
    Calendar,
    ArrowUpRight,
    ArrowDownRight,
    TrendingUp
} from 'lucide-react';
import { PageHeader } from './ui/PageHeader';
import { cn } from '../lib/utils';

const PLManager: React.FC = () => {
    const [statements, setStatements] = useState<any[]>([]);
    const [isAdding, setIsAdding] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        period: 'monthly',
        period_start: '',
        period_end: '',
        total_income: 0,
        total_expense: 0,
        notes: ''
    });
    const [file, setFile] = useState<File | null>(null);

    useEffect(() => { fetchStatements(); }, []);

    const fetchStatements = async () => {
        const res = await api.get('/pl-statements');
        setStatements(res.data);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const data = new FormData();
        Object.entries(formData).forEach(([k, v]) => data.append(k, v.toString()));
        if (file) data.append('file', file);

        try {
            await api.post('/pl-statements', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setIsAdding(false);
            fetchStatements();
            setFormData({ title: '', period: 'monthly', period_start: '', period_end: '', total_income: 0, total_expense: 0, notes: '' });
            setFile(null);
        } catch (e) { alert('Upload failed'); }
    };

    return (
        <div className="space-y-6">
            <PageHeader title="P&L Statements" subtitle="Financial transparency and reporting">
                <button
                    onClick={() => setIsAdding(!isAdding)}
                    className="bg-violet-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-violet-700 transition-all text-sm"
                >
                    {isAdding ? 'Cancel' : 'Upload Statement'}
                    <Plus size={18} className={cn("transition-transform", isAdding && "rotate-45")} />
                </button>
            </PageHeader>

            {isAdding && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-in slide-in-from-top-2 duration-300">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2 space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Statement Title</label>
                                <input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="e.g. October 2023 Financials" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Period</label>
                                <select value={formData.period} onChange={e => setFormData({...formData, period: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500">
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="annual">Annual</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Start Date</label>
                                <input type="date" value={formData.period_start} onChange={e => setFormData({...formData, period_start: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">End Date</label>
                                <input type="date" value={formData.period_end} onChange={e => setFormData({...formData, period_end: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Total Income</label>
                                <input type="number" value={formData.total_income} onChange={e => setFormData({...formData, total_income: parseFloat(e.target.value)})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Total Expense</label>
                                <input type="number" value={formData.total_expense} onChange={e => setFormData({...formData, total_expense: parseFloat(e.target.value)})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="md:col-span-2 space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Report File (PDF)</label>
                                <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100" />
                            </div>
                        </div>
                        <div className="flex justify-end pt-2">
                            <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all">
                                Upload Statement
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 text-left font-semibold text-slate-600">Statement</th>
                            <th className="px-6 py-4 text-left font-semibold text-slate-600">Period</th>
                            <th className="px-6 py-4 text-left font-semibold text-slate-600">Income</th>
                            <th className="px-6 py-4 text-left font-semibold text-slate-600">Expense</th>
                            <th className="px-6 py-4 text-left font-semibold text-slate-600">Net Balance</th>
                            <th className="px-6 py-4 text-right font-semibold text-slate-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {statements.map((s) => (
                            <tr key={s.id} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
                                            <FileText size={18} />
                                        </div>
                                        <span className="font-bold text-slate-900">{s.title}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-slate-500 capitalize">{s.period}</td>
                                <td className="px-6 py-4 text-emerald-600 font-medium">₹{s.total_income.toLocaleString()}</td>
                                <td className="px-6 py-4 text-red-500 font-medium">₹{s.total_expense.toLocaleString()}</td>
                                <td className="px-6 py-4">
                                    <div className={cn(
                                        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border",
                                        s.net_balance >= 0 ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-red-50 text-red-700 border-red-100"
                                    )}>
                                        {s.net_balance >= 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                                        ₹{Math.abs(s.net_balance).toLocaleString()}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        {s.file_url && (
                                            <a href={s.file_url} target="_blank" rel="noreferrer" className="p-1.5 text-slate-400 hover:text-violet-600">
                                                <Download size={16} />
                                            </a>
                                        )}
                                        <button className="p-1.5 text-slate-400 hover:text-red-500">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {statements.length === 0 && (
                    <div className="py-12 text-center">
                        <TrendingUp size={32} className="text-slate-200 mx-auto mb-2" />
                        <p className="text-sm text-slate-400 font-medium">No financial statements uploaded yet</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PLManager;
